import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const containerStyle = {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '2rem',
    lineHeight: '1.8',
    color: '#333'
  };

  const headingStyle = {
    color: '#2e7d32',
    marginTop: '2rem',
    marginBottom: '1rem'
  };

  const subHeadingStyle = {
    color: '#1b5e20',
    marginTop: '1.5rem',
    marginBottom: '0.5rem'
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    marginTop: '1rem',
    marginBottom: '1rem'
  };

  const thStyle = {
    background: '#e8f5e9',
    padding: '0.75rem',
    textAlign: 'left',
    borderBottom: '2px solid #2e7d32'
  };

  const tdStyle = {
    padding: '0.75rem',
    borderBottom: '1px solid #ddd'
  };

  return (
    <div style={containerStyle}>
      <h1 style={{...headingStyle, textAlign: 'center', fontSize: '2.5rem'}}>
        Privacy Policy
      </h1>
      
      <p style={{textAlign: 'center', color: '#666', marginBottom: '2rem'}}>
        Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
      </p>

      <div style={{background: '#f5f5f5', padding: '1.5rem', borderRadius: '8px', marginBottom: '2rem'}}>
        <p style={{margin: 0}}>
          <strong>Your Privacy Matters</strong><br/>
          Thornbee LLC ("we," "us," or "our") operates Gene, the genealogical research assistant powered 
          by Generational Nexus (GNology™). This Privacy Policy explains how we collect, use, disclose, 
          and safeguard your information when you use our Service.
        </p>
      </div>

      <h2 style={headingStyle}>1. Information We Collect</h2>
      
      <h3 style={subHeadingStyle}>1.1 Information You Provide</h3>
      <p>We collect information you directly provide to us, including:</p>
      <ul>
        <li><strong>Account Information:</strong> Name, email address, phone number, password</li>
        <li><strong>Genealogical Data:</strong> Family names, relationships, dates, locations, and historical information</li>
        <li><strong>Documents:</strong> Photos, certificates, and records you upload for analysis</li>
        <li><strong>Communication Data:</strong> Your questions, research goals, and interactions with Gene</li>
        <li><strong>Payment Information:</strong> Processed securely through Stripe (we don't store credit card details)</li>
      </ul>

      <h3 style={subHeadingStyle}>1.2 Information Collected Automatically</h3>
      <p>When you use Gene, we automatically collect:</p>
      <ul>
        <li><strong>Usage Data:</strong> Features used, time spent, search queries</li>
        <li><strong>Device Information:</strong> Browser type, operating system, IP address</li>
        <li><strong>Log Data:</strong> Access times, pages viewed, app crashes</li>
        <li><strong>Cookies:</strong> Session management and preference storage</li>
      </ul>

      <h2 style={headingStyle}>2. How We Use Your Information</h2>
      <p>We use the collected information to:</p>
      <ul>
        <li>Provide and maintain the Gene service</li>
        <li>Process your genealogical research requests</li>
        <li>Analyze documents and provide research recommendations</li>
        <li>Improve and personalize your experience</li>
        <li>Communicate with you about your account and updates</li>
        <li>Process payments and manage subscriptions</li>
        <li>Ensure security and prevent fraud</li>
        <li>Comply with legal obligations</li>
      </ul>

      <h2 style={headingStyle}>3. Legal Basis for Processing (GDPR)</h2>
      <p>For users in the European Economic Area (EEA), we process your data based on:</p>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Purpose</th>
            <th style={thStyle}>Legal Basis</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}>Providing Gene services</td>
            <td style={tdStyle}>Contract performance</td>
          </tr>
          <tr>
            <td style={tdStyle}>Account security</td>
            <td style={tdStyle}>Legitimate interests</td>
          </tr>
          <tr>
            <td style={tdStyle}>Marketing communications</td>
            <td style={tdStyle}>Consent</td>
          </tr>
          <tr>
            <td style={tdStyle}>Legal compliance</td>
            <td style={tdStyle}>Legal obligation</td>
          </tr>
        </tbody>
      </table>

      <h2 style={headingStyle}>4. Data Sharing and Disclosure</h2>
      <p>We do not sell, trade, or rent your personal information. We may share your information with:</p>
      
      <h3 style={subHeadingStyle}>4.1 Service Providers</h3>
      <ul>
        <li><strong>Anthropic:</strong> AI processing (anonymized queries only)</li>
        <li><strong>Stripe:</strong> Payment processing</li>
        <li><strong>Twilio:</strong> SMS verification</li>
        <li><strong>DigitalOcean:</strong> Cloud hosting</li>
      </ul>

      <h3 style={subHeadingStyle}>4.2 Legal Requirements</h3>
      <p>
        We may disclose your information if required by law, court order, or government request, or if 
        necessary to protect our rights, property, or safety.
      </p>

      <h2 style={headingStyle}>5. Data Security</h2>
      <p>
        We implement appropriate technical and organizational measures to protect your personal information, 
        including:
      </p>
      <ul>
        <li>Encryption of data in transit and at rest</li>
        <li>Regular security assessments</li>
        <li>Access controls and authentication</li>
        <li>Secure cloud infrastructure</li>
        <li>Regular backups and disaster recovery procedures</li>
      </ul>

      <h2 style={headingStyle}>6. Data Retention</h2>
      <p>We retain your information for as long as necessary to:</p>
      <ul>
        <li>Provide our services to you</li>
        <li>Comply with legal obligations</li>
        <li>Resolve disputes and enforce agreements</li>
      </ul>
      <p>
        Account data is retained while your account is active. You may request deletion at any time, 
        subject to legal retention requirements.
      </p>

      <h2 style={headingStyle}>7. Your Rights</h2>
      
      <h3 style={subHeadingStyle}>7.1 General Rights</h3>
      <p>You have the right to:</p>
      <ul>
        <li><strong>Access:</strong> Request a copy of your personal data</li>
        <li><strong>Correction:</strong> Update or correct inaccurate information</li>
        <li><strong>Deletion:</strong> Request deletion of your personal data</li>
        <li><strong>Portability:</strong> Receive your data in a machine-readable format</li>
        <li><strong>Restriction:</strong> Limit how we process your data</li>
        <li><strong>Objection:</strong> Object to certain processing activities</li>
      </ul>

      <h3 style={subHeadingStyle}>7.2 GDPR Rights (EEA Users)</h3>
      <p>
        If you're in the EEA, you have additional rights under GDPR, including the right to lodge a 
        complaint with your local supervisory authority.
      </p>

      <h3 style={subHeadingStyle}>7.3 CCPA Rights (California Residents)</h3>
      <p>
        California residents have additional rights under CCPA, including the right to know what personal 
        information we collect and the right to opt-out of any sale of personal information (note: we do 
        not sell personal information).
      </p>

      <h2 style={headingStyle}>8. Children's Privacy</h2>
      <p>
        Gene is not intended for children under 18. We do not knowingly collect personal information from 
        children under 18. If you believe we have collected information from a child, please contact us 
        immediately.
      </p>

      <h2 style={headingStyle}>9. Cookies and Tracking</h2>
      <p>We use cookies and similar tracking technologies to:</p>
      <ul>
        <li>Maintain your session</li>
        <li>Remember your preferences</li>
        <li>Analyze usage patterns</li>
        <li>Improve our services</li>
      </ul>
      <p>
        You can control cookies through your browser settings. Note that disabling cookies may limit 
        certain features of Gene.
      </p>

      <h2 style={headingStyle}>10. International Data Transfers</h2>
      <p>
        Your information may be transferred to and processed in the United States. We ensure appropriate 
        safeguards are in place for international transfers in compliance with applicable laws.
      </p>

      <h2 style={headingStyle}>11. Your California Privacy Rights</h2>
      <p>
        Under California Civil Code Section 1798.83, California residents may request information about 
        our disclosure of personal information to third parties for direct marketing purposes. We do not 
        disclose personal information to third parties for direct marketing purposes.
      </p>

      <h2 style={headingStyle}>12. Do Not Track Signals</h2>
      <p>
        Gene does not currently respond to Do Not Track signals, as there is no consistent industry 
        standard. We will update this policy if our practices change.
      </p>

      <h2 style={headingStyle}>13. Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you of any material changes 
        by posting the new Privacy Policy on this page and updating the "Last Updated" date. We encourage 
        you to review this Privacy Policy periodically.
      </p>

      <h2 style={headingStyle}>14. Contact Us</h2>
      <p>
        If you have questions about this Privacy Policy or want to exercise your rights, please contact us:
      </p>
      
      <div style={{background: '#f5f5f5', padding: '1.5rem', borderRadius: '8px', marginTop: '1rem'}}>
        <p style={{margin: '0 0 0.5rem 0'}}><strong>Thornbee LLC</strong></p>
        <p style={{margin: '0 0 0.5rem 0'}}>Email: privacy@mygnology.com</p>
        <p style={{margin: '0 0 0.5rem 0'}}>Support: support@mygnology.com</p>
        <p style={{margin: '0'}}>Website: https://mygnology.com</p>
      </div>

      <div style={{marginTop: '3rem', padding: '2rem', background: '#e8f5e9', borderRadius: '8px', textAlign: 'center'}}>
        <p style={{margin: '0 0 1rem 0', fontSize: '1.1rem'}}>
          We are committed to protecting your privacy and ensuring the security of your genealogical research.
        </p>
        <Link to="/research" style={{
          display: 'inline-block',
          padding: '0.75rem 2rem',
          background: '#2e7d32',
          color: 'white',
          borderRadius: '50px',
          textDecoration: 'none',
          fontWeight: '600',
          marginRight: '1rem'
        }}>
          Return to Gene →
        </Link>
        <Link to="/terms" style={{
          display: 'inline-block',
          padding: '0.75rem 2rem',
          background: 'white',
          color: '#2e7d32',
          border: '2px solid #2e7d32',
          borderRadius: '50px',
          textDecoration: 'none',
          fontWeight: '600'
        }}>
          View Terms of Service
        </Link>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
